import axios from "axios";
import { getToken } from "./auth.api";

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

api.interceptors.request.use(config => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_user");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

/* ── Cache em módulo ──────────────────────────────────────────────────
   TTL 30 s: múltiplas páginas de Tarefas e Projetos reutilizam a mesma
   lista de projetos sem re-fetch a cada navegação.
   bust=true (usado pelo auto-refresh) apaga a entrada antes de buscar.
   Escritas limpam todo o cache para garantir dados frescos.
──────────────────────────────────────────────────────────────────── */
const _cache   = new Map();
const _pending = new Map();
const TTL = 300_000; // 5 min — navegação usa cache; auto-refresh passa bust=true

async function cached(key, fn) {
  const hit = _cache.get(key);
  if (hit && Date.now() - hit.ts < TTL) return hit.data;
  if (_pending.has(key)) return _pending.get(key);
  const promise = fn()
    .then(data => { _cache.set(key, { data, ts: Date.now() }); _pending.delete(key); return data; })
    .catch(err  => { _pending.delete(key); throw err; });
  _pending.set(key, promise);
  return promise;
}

export function clearProjetosCache() { _cache.clear(); }

/* ── Leituras (cacheadas) ─────────────────────────────────────────── */

export const getProjetosAcompanhamento = (bust = false, tipo = null) => {
  const key = `proj_${tipo ?? "all"}`;
  if (bust) _cache.delete(key);
  return cached(key, () =>
    api.get("/projetos/acompanhamento", {
      params: tipo != null ? { tipo } : {},
    }).then(r => r.data)
  );
};

// Endpoint combinado: retorna { projetos, tarefas } numa única chamada.
// Se /projetos/todas não estiver disponível, cai de volta para N+1 requests.
export const getProjetosCompleto = (bust = false, tipo = null) => {
  const key = `completo_${tipo ?? "all"}`;
  if (bust) _cache.delete(key);
  return cached(key, async () => {
    try {
      const data = await api.get("/projetos/todas", {
        params: tipo != null ? { tipo } : {},
      }).then(r => r.data);

      // Valida formato esperado { projetos: [], tarefas: [] }
      if (data && Array.isArray(data.projetos) && Array.isArray(data.tarefas)) {
        return data;
      }
      throw new Error("formato inesperado");
    } catch {
      // Fallback: endpoint ainda não disponível — comportamento anterior (N+1)
      const projetos = await api.get("/projetos/acompanhamento", {
        params: tipo != null ? { tipo } : {},
      }).then(r => r.data);

      const ids = [...new Set((projetos || []).filter(r => r.ticketid).map(r => r.ticketid))];
      const results = await Promise.allSettled(
        ids.map(id => api.get("/tarefas", { params: { id_ticket: id } }).then(r => r.data))
      );
      const tarefas = results.flatMap(r =>
        r.status === "fulfilled" && Array.isArray(r.value) ? r.value : []
      );
      return { projetos, tarefas };
    }
  });
};

/* ── Escritas (invalidam cache) ───────────────────────────────────── */

export const criarProjeto = async (body) => {
  const r = await api.post("/projetos", body);
  _cache.clear();
  return r.data;
};

export const atualizarProjeto = async (id, body) => {
  const r = await api.put(`/projetos/${id}`, body);
  _cache.clear();
  return r.data;
};

export const excluirProjeto = async (id) => {
  const r = await api.delete(`/projetos/${id}`);
  _cache.clear();
  return r.data;
};

export const inativarProjeto = async (id) => {
  const r = await api.put(`/projetos/${id}/inativar`);
  _cache.clear();
  return r.data;
};

export const atualizarStatusProjeto = async (id, id_gp_status) => {
  const r = await api.put(`/projetos/${id}/status`, { id_gp_status });
  _cache.clear();
  return r.data;
};

export const atualizarImportancia = async (id, importancia) => {
  const r = await api.put(`/projetos/${id}/importancia`, { importancia });
  _cache.clear();
  return r.data;
};

export const vincularTicket = async (id_projeto, id_ticket, descricao_customizada = null) => {
  const r = await api.post(`/projetos/${id_projeto}/tickets`, { id_ticket, descricao_customizada });
  _cache.clear();
  return r.data;
};

export const desvincularTicket = async (id_projeto, id_ticket) => {
  const r = await api.delete(`/projetos/${id_projeto}/tickets/${id_ticket}`);
  _cache.clear();
  return r.data;
};

export const atualizarDescricaoTicket = async (id_projeto, id_ticket, descricao_customizada) => {
  const r = await api.put(`/projetos/${id_projeto}/tickets/${id_ticket}/descricao`, { descricao_customizada });
  _cache.clear();
  return r.data;
};
